import { useMemo, useState } from "react";

import Button from "@/components/Button";
import Checkbox from "@/components/Checkbox";
import RankedSelection from "@/components/RankedSelection";
import SearchInput from "@/components/SearchInput";
import { US_STATE_OPTIONS } from "./careerIntentCatalog";
import {
  moveRankedSelection,
  RANKED_LOCATION_HINT,
  rankedSelectionIndex,
  toggleRankedSelection
} from "./careerIntentForm";

function selectedStatesLabel(count: number) {
  return count > 0 ? `${count} state${count === 1 ? "" : "s"} selected` : "Select states…";
}

function rankedOptionLabel(label: string, rank?: number) {
  return rank != null ? `${rank}. ${label}` : label;
}

export default function CareerIntentStatePicker({
  disabled = false,
  onChange,
  value
}: {
  disabled?: boolean;
  onChange: (value: string[]) => void;
  value: string[];
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const normalizedSearch = search.trim().toLowerCase();
  const atMaxSelections = value.length >= 3;
  const summary = selectedStatesLabel(value.length);

  const visibleStates = useMemo(
    () =>
      US_STATE_OPTIONS.filter(
        (option) =>
          !normalizedSearch ||
          option.label.toLowerCase().includes(normalizedSearch) ||
          option.value.toLowerCase().includes(normalizedSearch)
      ),
    [normalizedSearch]
  );

  const toggleState = (stateCode: string) => {
    if (disabled) return;
    onChange(toggleRankedSelection(value, stateCode));
  };

  return (
    <div>
      <p className="mb-6 text-body-large text-sys-brand-on-surface-variant">{RANKED_LOCATION_HINT}</p>
      <RankedSelection
        className="mb-8"
        disabled={disabled}
        items={value.flatMap((stateCode) => {
          const state = US_STATE_OPTIONS.find((option) => option.value === stateCode);
          return state ? [{ value: state.value, label: state.label }] : [];
        })}
        onMove={(index, direction) => onChange(moveRankedSelection(value, index, direction))}
        onRemove={(stateCode) => onChange(toggleRankedSelection(value, stateCode))}
        testIdPrefix="career-intent-state"
      />

      <div
        className={isOpen ? "rounded-lg border border-sys-brand-outline p-4" : undefined}
        data-testid="career-intent-state-picker"
      >
        {isOpen ? (
          <div className="flex flex-col gap-6">
            <div className="flex min-h-12 w-full items-center justify-between gap-3">
              <span className="flex-1 text-body-large">{summary}</span>
              <Button
                type="button"
                ariaLabel="Close state picker"
                variant="text"
                testId="career-intent-state-picker-close"
                onClick={() => setIsOpen(false)}
              >
                Done
              </Button>
            </div>
            <div data-testid="career-intent-state-search">
              <SearchInput
                ariaLabel="Search states"
                pageSearchShortcut={false}
                placeholder="Search states"
                query={search}
                onChange={setSearch}
              />
            </div>
            <div className="flex flex-col gap-3" role="list">
              {visibleStates.map((state) => {
                const selected = value.includes(state.value);
                const optionDisabled = disabled || (!selected && atMaxSelections);
                const rank = rankedSelectionIndex(value, state.value);

                return (
                  <Checkbox
                    key={state.value}
                    checked={selected}
                    disabled={optionDisabled}
                    name={`career-intent-state-option-${state.value}`}
                    label={rankedOptionLabel(state.label, rank)}
                    onChange={(checked) => {
                      if (checked === selected) return;
                      toggleState(state.value);
                    }}
                  />
                );
              })}
            </div>
          </div>
        ) : (
          <Button
            type="button"
            variant="outlined"
            ariaLabel="Choose states"
            testId="career-intent-state-picker-toggle"
            disabled={disabled}
            icon="add"
            trailingIcon
            onClick={() => setIsOpen(true)}
          >
            {summary}
          </Button>
        )}
      </div>
    </div>
  );
}
